import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  CloudDownload as ImportIcon,
  InsertDriveFile as FileIcon,
  Refresh as RefreshIcon,
  SelectAll as SelectAllIcon,
  DeselectRounded as DeselectAllIcon,
  Cloud as S3Icon,
  School as MoodleIcon,
} from '@mui/icons-material';
import { basePrefixApi, environmentApi, EnvironmentFile, EnvironmentS3Config } from '../services/api';
import { useMoodleImport } from '../App';
import { useJobs } from '../contexts/JobsContext';

interface ImportFromEnvironmentDialogProps {
  open: boolean;
  onClose: () => void;
  currentEnvironment: string;
  targetFolder: string;
  onImportComplete: () => void;
}

const ImportFromEnvironmentDialog: React.FC<ImportFromEnvironmentDialogProps> = ({
  open,
  onClose,
  currentEnvironment,
  targetFolder,
  onImportComplete,
}) => {
  const { openMoodleImport } = useMoodleImport();
  const { setShowJobsPanel } = useJobs();
  const [importSource, setImportSource] = useState<'s3' | 'moodle'>('s3');
  const [environments, setEnvironments] = useState<string[]>([]);
  const [sourceEnvironment, setSourceEnvironment] = useState('');
  const [files, setFiles] = useState<EnvironmentFile[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [environmentConfigs, setEnvironmentConfigs] = useState<Record<string, EnvironmentS3Config>>({});

  // Open Moodle dialog when source changes to 'moodle'
  useEffect(() => {
    if (open && importSource === 'moodle') {
      openMoodleImport({
        targetFolder: targetFolder as 'courses' | 'competency_frameworks' | 'learning_path_templates',
        targetEnvironment: currentEnvironment,
        onComplete: onImportComplete,
      });
      // Close this dialog since Moodle dialog is now handling the import
      onClose();
      // Reset back to S3 for next time
      setImportSource('s3');
    }
  }, [open, importSource, targetFolder, currentEnvironment, onImportComplete, openMoodleImport, onClose]);

  // Load environments when dialog opens
  useEffect(() => {
    if (open) {
      loadEnvironments();
      setSourceEnvironment('');
      setFiles([]);
      setSelectedFiles([]);
      setError(null);
      setSuccess(null);
    }
  }, [open]);

  // Load files when source environment changes
  useEffect(() => {
    if (sourceEnvironment && open) {
      loadFiles();
    }
  }, [sourceEnvironment, open]);

  const loadEnvironments = async () => {
    try {
      const envs = await basePrefixApi.getBasePrefixes();
      // Filter out current environment
      const availableEnvs = envs.filter(env => env !== currentEnvironment);
      setEnvironments(availableEnvs);
      
      // Load S3 configs for all environments
      const configs: Record<string, EnvironmentS3Config> = {};
      await Promise.all(
        availableEnvs.map(async (env) => {
          try {
            const config = await environmentApi.getEnvironmentConfig(env);
            configs[env] = config;
          } catch (err) {
            console.error(`Failed to load config for ${env}:`, err);
          }
        })
      );
      setEnvironmentConfigs(configs);
    } catch (err) {
      setError('Failed to load environments');
      console.error('Error loading environments:', err);
    }
  };

  const loadFiles = async () => {
    if (!sourceEnvironment) return;

    setLoadingFiles(true);
    setError(null);
    try {
      const envFiles = await environmentApi.getEnvironmentFolderFiles(sourceEnvironment, targetFolder);
      setFiles(envFiles);
      setSelectedFiles([]); // Clear selection when loading new files
    } catch (err) {
      setError(`Failed to load files from ${sourceEnvironment}`);
      console.error('Error loading files:', err);
      setFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  };

  const refreshFiles = async () => {
    await loadFiles();
  };

  const handleFileToggle = (fileName: string) => {
    setSelectedFiles(prev => 
      prev.includes(fileName)
        ? prev.filter(f => f !== fileName)
        : [...prev, fileName]
    );
  };

  const handleSelectAll = () => {
    setSelectedFiles(files.map(f => f.name));
  };

  const handleSelectNone = () => {
    setSelectedFiles([]);
  };

  const handleImport = async () => {
    if (!sourceEnvironment || selectedFiles.length === 0) {
      setError('Please select files to import');
      return;
    }

    // Close dialog immediately and show jobs panel
    onClose();
    setShowJobsPanel(true);

    try {
      // Use the streaming clone API with specific file selections for this folder
      const selections = {
        [targetFolder]: selectedFiles,
      };
      
      const apiUrl = (import.meta as any).env?.VITE_API_URL || '/api';
      const response = await fetch(`${apiUrl}/environments/clone-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ source: sourceEnvironment, target: currentEnvironment, selections }),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to start clone operation');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('No response body');
      }

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const text = decoder.decode(value);
        const lines = text.split('\n\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = JSON.parse(line.slice(6));
            
            if (data.type === 'job-created') {
              console.log('Clone job created:', data.jobId);
            } else if (data.type === 'complete') {
              console.log('Clone completed:', data.message);
              onImportComplete();
            } else if (data.type === 'error') {
              console.error('Clone error:', data.error);
            }
          }
        }
      }
    } catch (err) {
      console.error('Error importing files:', err);
      setError('Failed to import files');
    } finally {
      setLoading(false);
    }
  };

  const getFolderDisplayName = (folder: string) => {
    const folderNames: Record<string, string> = {
      'categories': 'Categories',
      'courses': 'Courses',
      'competency_frameworks': 'Competency Frameworks',
      'learning_path_templates': 'Learning Path Templates',
      'root': 'Root Files',
    };
    return folderNames[folder] || folder;
  };

  return (
    <>
      <Dialog open={open && importSource === 's3'} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <ImportIcon />
            Import Files to {getFolderDisplayName(targetFolder)}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3} pt={1}>
            {/* Source Type Toggle */}
            <Box display="flex" justifyContent="center">
              <ToggleButtonGroup
                value={importSource}
                exclusive
                onChange={(_, newValue) => {
                  if (newValue !== null) {
                    setImportSource(newValue);
                  }
                }}
                aria-label="import source"
              >
                <ToggleButton value="s3" aria-label="S3 environment">
                  <S3Icon sx={{ mr: 1 }} />
                  S3 Environment
                </ToggleButton>
                <ToggleButton value="moodle" aria-label="Moodle source">
                  <MoodleIcon sx={{ mr: 1 }} />
                  Moodle Source
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
          
          {success && (
            <Alert severity="success" onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          {/* Source Environment Selection */}
          <FormControl fullWidth>
            <InputLabel>Import from Environment</InputLabel>
            <Select
              value={sourceEnvironment}
              onChange={(e) => setSourceEnvironment(e.target.value)}
              label="Import from Environment"
            >
              {environments.length === 0 ? (
                <MenuItem disabled>No other environments available</MenuItem>
              ) : (
                environments.map((env) => {
                  const config = environmentConfigs[env];
                  const bucketName = config?.awsS3Bucket || 'Global Bucket';
                  const usingCustom = config && !config.usingGlobal;
                  
                  return (
                    <MenuItem key={env} value={env}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                        <Typography>{env}</Typography>
                        <Chip 
                          label={bucketName} 
                          size="small" 
                          variant="outlined"
                          color={usingCustom ? 'secondary' : 'default'}
                          sx={{ ml: 2 }}
                        />
                      </Box>
                    </MenuItem>
                  );
                })
              )}
            </Select>
          </FormControl>

          {/* File Selection */}
          {sourceEnvironment && (
            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="subtitle1">
                  Available Files ({files.length} files)
                </Typography>
                <Box display="flex" gap={1}>
                  <Chip 
                    label={`${selectedFiles.length} selected`} 
                    color={selectedFiles.length > 0 ? 'primary' : 'default'}
                    size="small"
                  />
                  <Tooltip title="Refresh Files">
                    <IconButton size="small" onClick={refreshFiles} disabled={loadingFiles}>
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {loadingFiles ? (
                <Box display="flex" alignItems="center" gap={2} py={4} justifyContent="center">
                  <CircularProgress size={24} />
                  <Typography>Loading files from {sourceEnvironment}...</Typography>
                </Box>
              ) : files.length > 0 ? (
                <Box>
                  {/* Selection Actions */}
                  <Box display="flex" gap={1} mb={2}>
                    <Button
                      size="small"
                      startIcon={<SelectAllIcon />}
                      onClick={handleSelectAll}
                      disabled={selectedFiles.length === files.length}
                    >
                      Select All ({files.length})
                    </Button>
                    <Button
                      size="small"
                      startIcon={<DeselectAllIcon />}
                      onClick={handleSelectNone}
                      disabled={selectedFiles.length === 0}
                    >
                      Select None
                    </Button>
                  </Box>

                  {/* File List */}
                  <List 
                    dense 
                    sx={{ 
                      maxHeight: 400, 
                      overflow: 'auto',
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    {files.map((file) => (
                      <ListItem 
                        key={file.key} 
                        sx={{ py: 0.5 }}
                        secondaryAction={
                          <Typography variant="caption" color="textSecondary">
                            {(file.size / 1024).toFixed(1)} KB
                          </Typography>
                        }
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Checkbox
                            checked={selectedFiles.includes(file.name)}
                            onChange={() => handleFileToggle(file.name)}
                          />
                        </ListItemIcon>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <FileIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={file.name}
                          secondary={new Date(file.lastModified).toLocaleDateString()}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              ) : (
                <Box textAlign="center" py={4}>
                  <Typography color="textSecondary">
                    No files found in {getFolderDisplayName(targetFolder)} folder of {sourceEnvironment}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={!sourceEnvironment || selectedFiles.length === 0 || loading}
          startIcon={loading ? <CircularProgress size={16} /> : <ImportIcon />}
        >
          {loading ? 'Importing...' : `Import ${selectedFiles.length} Files`}
        </Button>
      </DialogActions>
      </Dialog>
    </>
  );
};

export default ImportFromEnvironmentDialog;